// CONVERT v2 — batch 3's rebuild of screens/Swap.tsx (UI rebuild; drawn when lib/ui-v2.ts switches batch 3 on).
//
// Astra's convert: the first decision is WHOSE balance — "Party balance" (one asset into another on their account) or
// "Desk balance" (through a money changer) — two doors, and this viewer's recent picks under them. A door opens its list
// (search, the rows in the page — no box that scrolls inside the phone); a row opens its form, which REPLACES the
// chooser; "Change" goes back to the list. The two mechanisms stay two forms: a party's box converts on account
// (components/v2/convert/PartyForm — nothing moves), a desk account converts as a settled trade with whoever changed the
// money (DeskForm).
//
// KEPT FROM Swap.tsx: the URL is the selection (?party=<slug> · ?rail=<slug>&asset=<ASSET>, so a row can be linked to and
// survives a reload; ?entity= is the desk and switching desk clears the selection); the two reads and their keys; the
// rows' rules (Chooser.tsx); a failed read — including `balances_read: false` inside a 200 — is one line with Try again,
// never "Nothing to convert."; that claim only from both reads whole (a roster cut at its page says so); the picked row
// found by exact code first, then by the fold; a wallet at 0 with money on the chain points at Chain to book it first.
// New: ?pick=party|desk is the open list (so Back walks list ← form); the recent picks live in this browser only.
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Users, Wallet } from 'lucide-react';
import { api, type PartySummary, type TreasuryRow } from '../../lib/api';
import { DeskSwitch, useDesk, type Desk } from '../../components/DeskSwitch';
import { EmptyState, PageHead, Panel, Pending, ReadFailed, SearchBar, StateLine, TAP, Tile } from '../../components/v2/kit';
import { partyName, railName, railKindOrder, sameAsset, foldAsset } from '../../lib/naming';
import { displayAsset } from '../../lib/format';
import { BALANCES_UNREAD, balancesUnread } from '../../lib/balances-read';
import { rosterCut } from '../../lib/roster-cut';
import { PartyConvertForm } from '../../components/v2/convert/PartyForm';
import { DeskConvertForm, FromAccount } from '../../components/v2/convert/DeskForm';
import { UnbookedTrades } from '../../components/UnbookedTrades';
import { PartyRows, RailRows, venueHolds, chainHolds, chainWords } from '../../components/v2/convert/Chooser';
import { FormCard, readRecent, rememberRecent, type RecentPick } from '../../components/v2/convert/layout';

type Picked =
  | { kind: 'party'; slug: string }
  | { kind: 'rail'; slug: string; asset: string }
  | null;

/** "usdt-trc", "USDT·TRC", "usdttrc" → "usdttrc": one spelling for the asset match in the search box (RailPicker's rule). */
const norm = (x: string) => x.toLowerCase().replace(/[^a-z0-9]/g, '');

export function ConvertV2() {
  const [entity] = useDesk();
  const [params, setParams] = useSearchParams();
  const picked: Picked = useMemo(() => {
    const party = params.get('party');
    const rail = params.get('rail');
    const asset = params.get('asset');
    if (party) return { kind: 'party', slug: party };
    if (rail && asset) return { kind: 'rail', slug: rail, asset };
    return null;
  }, [params]);
  const list = params.get('pick') === 'desk' ? 'desk' : params.get('pick') === 'party' ? 'party' : null;

  /** A link inside this screen: the desk kept, everything else replaced. */
  const href = (set: Record<string, string>) => {
    const p = new URLSearchParams();
    if (entity !== 'dubaiotc') p.set('entity', entity);
    for (const [k, v] of Object.entries(set)) p.set(k, v);
    const s = p.toString();
    return `/swap${s ? `?${s}` : ''}`;
  };
  const go = (set: Record<string, string>) => {
    const p = new URLSearchParams();
    if (entity !== 'dubaiotc') p.set('entity', entity);
    for (const [k, v] of Object.entries(set)) p.set(k, v);
    setParams(p);
  };
  // Switching desk clears the selection — the row belonged to the other desk's books.
  const switchDesk = (d: Desk) => {
    const p = new URLSearchParams();
    if (d !== 'dubaiotc') p.set('entity', d);
    setParams(p, { replace: true });
  };
  const [filter, setFilter] = useState('');
  const [recent, setRecent] = useState<RecentPick[]>(() => readRecent());

  const parties = useQuery({
    queryKey: ['parties', 'swap', entity],
    queryFn:  () => api.listParties({ entity, with_balances: true, pageSize: 500 }),
  });
  const treasury = useQuery({
    queryKey: ['treasury', 'balances', entity],
    queryFn:  () => api.treasuryBalances(entity),
  });
  const rows = treasury.data?.rows ?? [];

  // TWO ACCOUNTS UNDER ONE NAME TELL THEM APART BY THE ASSET — counted before the search narrows the list.
  const sharedName = useMemo(() => {
    const n = new Map<string, number>();
    for (const r of rows) {
      if (!r.slug || !r.asset || !(r.balance !== 0 || venueHolds(r) || chainHolds(r))) continue;
      n.set(railName(r), (n.get(railName(r)) ?? 0) + 1);
    }
    return (r: TreasuryRow) => (n.get(railName(r)) ?? 0) > 1;
  }, [rows]);

  const q = filter.trim().toLowerCase();
  const partyRows = useMemo(() =>
    (parties.data?.parties ?? [])
      .filter((p) => p.party_type !== 'own_account' && Object.values(p.balances ?? {}).some((v) => v !== 0))
      .filter((p) => !q || partyName(p).toLowerCase().includes(q) || p.slug.toLowerCase().includes(q))
      .sort((a, b) => partyName(a).localeCompare(partyName(b)))
  , [parties.data, q]);
  const railGroups = useMemo(() => {
    const code = norm(q);
    const listed = rows
      .filter((r) => !!r.slug && !!r.asset && (r.balance !== 0 || venueHolds(r) || chainHolds(r)))
      .filter((r) => !q
        || railName(r).toLowerCase().includes(q)
        || (r.label ?? '').toLowerCase().includes(q)
        || (r.slug ?? '').toLowerCase().includes(q)
        || code === norm(r.asset!) || code === norm(foldAsset(r.asset)) || code === norm(displayAsset(r.asset)))
      .sort((a, b) => railName(a).localeCompare(railName(b)) || (a.asset ?? '').localeCompare(b.asset ?? ''));
    const grouped: Record<string, TreasuryRow[]> = {};
    for (const r of listed) {
      const k = r.treasuryKind ?? 'other';
      (grouped[k] ??= []).push(r);
    }
    return Object.keys(grouped)
      .sort((a, b) => railKindOrder(a) - railKindOrder(b))
      .map((k) => ({ kind: k, rows: grouped[k]! }));
  }, [rows, q]);

  const pickedRow: TreasuryRow | null = picked?.kind === 'rail'
    ? rows.find((r) => r.slug === picked.slug && r.asset === picked.asset)   // dialect-ok: the URL carries the row's own code
      ?? rows.find((r) => r.slug === picked.slug && sameAsset(r.asset, picked.asset)) ?? null
    : null;

  // A read that failed is not an empty list; one inside a 200 (`balances_read: false`) is a failed read too.
  const unreadBalances = balancesUnread(parties.data);
  const readFailure = parties.isError ? parties.error : treasury.isError ? treasury.error : unreadBalances ? BALANCES_UNREAD : null;
  const readFailed = parties.isError || treasury.isError || unreadBalances;
  const readWhat = parties.isError || treasury.isError ? 'the list' : 'the balances';
  const retryReads = () => {
    if (parties.isError || unreadBalances) void parties.refetch();
    if (treasury.isError) void treasury.refetch();
  };
  // "Nothing to convert" is a claim: both reads SUCCEEDED, whole (Appendix B).
  const rosterCutShort = rosterCut(parties.data);
  const listRead = parties.isSuccess && treasury.isSuccess && !unreadBalances && !rosterCutShort;

  // A new view starts at its top (the form replaces the list; the list replaces the doors).
  const viewKey = `${list ?? ''}|${picked ? (picked.kind === 'party' ? picked.slug : `${picked.slug}:${picked.asset}`) : ''}`;
  useEffect(() => {
    document.querySelector('main')?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [viewKey]);

  const pickParty = (p: PartySummary) => {
    rememberRecent({ kind: 'party', slug: p.slug, name: partyName(p) });
    setRecent(readRecent());
    go({ party: p.slug });
  };
  const pickRail = (r: TreasuryRow) => {
    rememberRecent({ kind: 'rail', slug: r.slug!, asset: r.asset!, name: sharedName(r) ? `${railName(r)} · ${foldAsset(r.asset)}` : railName(r) });
    setRecent(readRecent());
    go({ rail: r.slug!, asset: r.asset! });
  };
  const desk = <DeskSwitch value={entity} onChange={switchDesk} />;

  // ── A PARTY PICKED: its form replaces the chooser.
  if (picked?.kind === 'party') {
    return (
      <>
        <PageHead title="Convert balance" actions={desk} />
        <PartyConvertForm
          key={`${entity}|${picked.slug}`}
          slug={picked.slug}
          entity={entity}
          change={<Link to={href({ pick: 'party' })} className={`${TAP} shrink-0`}>Change</Link>}
        />
      </>
    );
  }

  // ── A DESK ACCOUNT PICKED.
  if (picked?.kind === 'rail') {
    const change = <Link to={href({ pick: 'desk' })} className={`${TAP} shrink-0`}>Change</Link>;
    return (
      <>
        <PageHead title="Convert" actions={desk} />
        {pickedRow && pickedRow.balance === 0 && chainHolds(pickedRow) ? (
          // A WALLET AT ZERO WITH MONEY ON THE CHAIN: nothing to convert yet — book the deposit on the Chain screen first.
          <FormCard>
            <FromAccount row={pickedRow} slug={picked.slug} asset={picked.asset} change={change} />
            <StateLine tone="warn" action={<Link to="/chain" className={TAP}>Book the money that moved in →</Link>}>
              {chainWords(pickedRow)}
            </StateLine>
          </FormCard>
        ) : (
          <DeskConvertForm
            key={`${entity}|${picked.slug}|${pickedRow?.asset ?? picked.asset}`}
            entity={entity}
            from={{ slug: picked.slug, asset: pickedRow?.asset ?? picked.asset }}
            change={change}
          />
        )}
      </>
    );
  }

  // ── THE CHOOSER: the two doors, then the open list or the recent picks.
  const doors = (
    <div className="mb-4 grid gap-3 md:grid-cols-2 lg:mb-6 lg:gap-4">
      <div className={list === 'party' ? 'rounded-[10px] ring-1 ring-tg-accent' : ''}>
        <Tile to={href({ pick: 'party' })} icon={Users} title="Party balance" sub="One asset into another on their account" />
      </div>
      <div className={list === 'desk' ? 'rounded-[10px] ring-1 ring-tg-accent' : ''}>
        <Tile to={href({ pick: 'desk' })} icon={Wallet} title="Desk balance" sub="Convert through a money changer" />
      </div>
    </div>
  );

  const reads = (
    <>
      {readFailed && <div className="px-4 pt-4 lg:px-5"><ReadFailed what={readWhat} error={readFailure} onRetry={retryReads} /></div>}
      {(parties.isPending || treasury.isPending) && !readFailed && <div className="px-4 py-4 lg:px-5"><Pending inline /></div>}
      {rosterCutShort && <div className="px-4 pt-4 lg:px-5"><StateLine>Only the first {parties.data?.parties.length} parties are listed.</StateLine></div>}
    </>
  );
  const nothing = (empty: boolean) => listRead && empty && (
    <div className="px-4 py-4 lg:px-5">
      <EmptyState action={q ? <button type="button" className={TAP} onClick={() => setFilter('')}>Clear search</button> : undefined}>
        {q ? 'Nothing matches.' : 'Nothing to convert.'}
      </EmptyState>
    </div>
  );

  return (
    <>
      <PageHead title="Convert" actions={desk} />
      {doors}

      {list === null && (
        <UnbookedTrades entity={entity} frame={(title, count, body) => (
          <Panel title={title} aside={count ? count : undefined} flush className="mb-4 lg:mb-6">{body}</Panel>
        )} />
      )}

      {list === null && recent.length > 0 && (
        <Panel title="Recent contexts" flush>
          <ul>
            {recent.map((r) => (
              <li key={r.kind === 'party' ? `p:${r.slug}` : `r:${r.slug}:${r.asset}`} className="border-b border-stroke-soft last:border-b-0">
                <Link to={r.kind === 'party' ? href({ party: r.slug }) : href({ rail: r.slug, asset: r.asset })}
                  className="flex min-h-[64px] items-center justify-between gap-3 px-4 py-3 hover:bg-panel-2 lg:px-5">
                  <span className="min-w-0">
                    <span className="block truncate text-body font-medium">{r.name}</span>
                    <span className="mt-0.5 block text-meta text-tg-hint">{r.kind === 'party' ? 'Party balance' : `Desk account · ${displayAsset(r.asset)}`}</span>
                  </span>
                  <span className="btn-link shrink-0 text-body">Convert →</span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {list !== null && (
        <>
          <SearchBar value={filter} onChange={setFilter} placeholder={list === 'party' ? 'Search parties' : 'Search accounts or assets'} />
          <Panel title={list === 'party' ? 'Party balances' : 'Desk accounts'} flush>
            {reads}
            {list === 'party'
              ? (<>{partyRows.length > 0 && <PartyRows rows={partyRows} onPick={pickParty} />}{nothing(partyRows.length === 0)}</>)
              : (<>{railGroups.length > 0 && <RailRows groups={railGroups} sharedName={sharedName} onPick={pickRail} />}{nothing(railGroups.length === 0)}</>)}
          </Panel>
        </>
      )}
    </>
  );
}
